import React, { useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ProductBannerSlider = () => {
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    fade: true,
    cssEase: 'ease-in-out',
    pauseOnHover: true,
    arrows: false,
    beforeChange: (current, next) => setCurrentSlide(next)
  };

  // Slides with product image on left, text on right
  const slides = [
    {
      id: 1,
      image: '/images/site/site_24.png',
      link: '/shop/data-cables',
      title: 'MAGNET CABLE',
      subtitle: 'Premium Charging Experience',
      description: 'Engineered for excellence. Type-C to Type-C connectivity with superior durability.',
      alt: 'RSD Magnet Cable - Type-C to Type-C',
      type: 'split'
    },
    {
      id: 2,
      image: '/images/site/site_22.png',
      link: '/shop/earbuds',
      title: 'SONIC PRO',
      subtitle: 'Premium Music Experience',
      description: 'Immerse yourself in crystal-clear sound. 60H playtime. True wireless freedom.',
      alt: 'RSD Sonic Pro - Premium Wireless Earbuds',
      type: 'split-sonic'
    },
    {
      id: 3,
      image: '/images/site/site_23.jpg',
      link: '/shop/mobile-chargers',
      title: 'SILVER JACK',
      titleLine2: 'Super VOOC Charger',
      subtitle: 'Experience the Premium Range of RSD Charger',
      description: 'Lightning-fast charging with superior build quality. Designed for modern devices.',
      alt: 'Silver Jack Super VOOC Charger',
      type: 'split-silver'
    }
  ];

  const goToNext = () => sliderRef.current?.slickNext();
  const goToPrev = () => sliderRef.current?.slickPrev();

  return (
    <section className="relative w-full" data-testid="product-banner-slider">
      {/* Visual Divider from Hero */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-[#FF4D00] to-transparent" />
      
      {/* Compact Strip-Style Banner Container */}
      <div className="relative bg-gradient-to-r from-neutral-950 via-black to-neutral-950 overflow-hidden">
        <Slider ref={sliderRef} {...settings}>
          {slides.map((slide, index) => (
            <div key={slide.id} className="outline-none">
              <Link to={slide.link} className="block">
                {/* Sleek strip layout */}
                <div className="relative w-full h-[160px] sm:h-[180px] md:h-[200px] lg:h-[220px] overflow-hidden">
                  {/* Split layout: Product Left, Text Right */}
                  <div className="max-w-7xl mx-auto h-full flex items-center px-4 sm:px-6 lg:px-8">
                    {/* Left: Product Image */}
                    <div className={`w-1/3 sm:w-2/5 h-full flex items-center justify-center transition-all duration-700 ${
                      currentSlide === index ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                    }`}>
                      <img 
                        src={slide.image}
                        alt={slide.alt}
                        className="h-[90%] w-auto object-contain drop-shadow-2xl"
                      />
                    </div>
                    
                    {/* Right: Text Content */}
                    <div className={`w-2/3 sm:w-3/5 pl-4 sm:pl-8 transition-all duration-700 delay-100 ${
                      currentSlide === index ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                    }`}>
                      <p className="text-[#FF4D00] text-xs sm:text-sm font-semibold tracking-widest mb-1">
                        RSD ACCESSORIES
                      </p>
                      
                      {/* Title with special styling based on type */}
                      {slide.type === 'split-sonic' ? (
                        <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black tracking-tight mb-1 sm:mb-2">
                          <span className="text-transparent bg-clip-text bg-gradient-to-b from-white via-neutral-300 to-neutral-500">SONIC </span>
                          <span className="text-[#7FFF00]">PRO</span>
                        </h3>
                      ) : slide.type === 'split-silver' ? (
                        <div className="mb-1 sm:mb-2">
                          <h3 className="text-transparent bg-clip-text bg-gradient-to-b from-white via-neutral-300 to-neutral-500 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black tracking-tight">
                            {slide.title}
                          </h3>
                          <h4 className="text-white text-lg sm:text-xl md:text-2xl font-bold tracking-tight">
                            {slide.titleLine2}
                          </h4>
                        </div>
                      ) : (
                        <h3 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black tracking-tight mb-1 sm:mb-2">
                          {slide.title}
                        </h3>
                      )}
                      
                      <p className="text-neutral-300 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">
                        {slide.subtitle}
                      </p>
                      <p className="text-neutral-500 text-xs sm:text-sm hidden sm:block max-w-md">
                        {slide.description}
                      </p>
                      {/* Accent Line */}
                      <div className={`w-12 h-1 mt-2 sm:mt-3 rounded-full ${slide.type === 'split-sonic' ? 'bg-[#7FFF00]' : 'bg-[#FF4D00]'}`} />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </Slider>

        {/* Compact Navigation Arrows */}
        <button
          onClick={(e) => { e.preventDefault(); goToPrev(); }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-9 sm:h-9 bg-black/50 hover:bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white/70 hover:text-white transition-all duration-300"
          aria-label="Previous slide"
          data-testid="banner-prev-btn"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <button
          onClick={(e) => { e.preventDefault(); goToNext(); }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-9 sm:h-9 bg-black/50 hover:bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white/70 hover:text-white transition-all duration-300"
          aria-label="Next slide"
          data-testid="banner-next-btn"
        >
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* Custom Styles */}
        <style>{`
          [data-testid="product-banner-slider"] .slick-dots {
            bottom: 8px;
            z-index: 20;
          }
          [data-testid="product-banner-slider"] .slick-dots li {
            margin: 0 3px;
          }
          [data-testid="product-banner-slider"] .slick-dots li button:before {
            font-size: 0;
            width: 16px;
            height: 3px;
            background: rgba(255,255,255,0.3);
            border-radius: 2px;
            content: '';
            display: block;
            transition: all 0.4s ease;
          }
          [data-testid="product-banner-slider"] .slick-dots li.slick-active button:before {
            background: #FF4D00;
            width: 24px;
          }
          [data-testid="product-banner-slider"] .slick-dots li button {
            padding: 4px;
          }
          [data-testid="product-banner-slider"] .slick-slide {
            outline: none;
          }
        `}</style>
      </div>
    </section>
  );
};

export default ProductBannerSlider;
